import { useContext, useEffect, useState } from 'react';
import instance from '../config/axios';
import {
  HandleLoginMap,
  PatientMedicationResponse,
  PrescriptionStatus,
  TriggerPopup,
} from '../interface/interface';
import { useAuth } from 'react-oidc-context';
import Loading from '../components/Loading';
import moment from 'moment';
import Select, { SingleValue, components } from 'react-select';
import { UserContext } from '../context/userContext';
import {
  filterPrescriptionStatus,
  renderStatusColor,
} from '../constant/constant';
import CustomModal from '../components/CustomModal';
import {
  deletePrescription,
  requestForDeletePrescription,
  sendEPrescription,
  updatePrescriptionPreferable,
} from '../service';
import CustomQR from '../components/CustomQR';
import {
  CancelScriptIcon,
  DownCaretIcon,
  QRicon,
  ShareIcon,
} from '../assets/svgImages';
import showToast from '../components/ToastComponent';
import { PrescriptionDetail } from '../components/PrescriptionDetails';
import SingleRowTable from '../components/SingleRowTable';
import { Tooltip } from '../components/Tooltip';
import Toggle from '../components/ToggleComponent';

const PatientPrescriptions = () => {
  const { user } = useAuth();
  const { userDetail, setLoadingEnable } = useContext(UserContext);

  const [state, setState] = useState<HandleLoginMap<'ResultMap'>>({
    showResult: false,
    data: [],
    error: null,
  });

  const [filter, setFilter] = useState<
    SingleValue<{
      label: string;
      value: string;
    }>
  >({ label: 'Active', value: 'active' });
  const [isFetching, setIsFetching] = useState(false);

  const [open, setOpen] = useState<boolean>(false);
  const [requestOpen, setRequestOpen] = useState<boolean>(false);
  const [prescriptionId, setPrescriptionId] = useState<number>();
  const [reason, setReason] = useState<string>();
  const [triggerPopup, setTrigger] = useState<TriggerPopup>();
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [triggerPreferable, setTriggerPreferable] = useState<{
    id: number;
    isPreferable: boolean;
  }>();

  const [qrModal, OpenQRmodal] = useState<{
    isModalOpen: boolean;
    token: string | undefined;
  }>({ isModalOpen: false, token: undefined });

  const [showPreferTooltip, setShowPreferTooltip] = useState<string>();

  const callPrescription = async () => {
    try {
      setIsFetching(true);
      const token = user?.access_token;
      const response = await instance.get(
        `/api/v1/prescriptions?filter=${filter?.value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const responseData = response?.data as Array<PatientMedicationResponse>;

      const productCount: { [key: number]: number } = {};

      // Count the occurrences of each product
      responseData.forEach((prescription) => {
        const productId = prescription.product.id;
        if (productCount[productId]) {
          productCount[productId]++;
        } else {
          productCount[productId] = 1;
        }
      });

      // Add the flag if the product occurs more than once
      responseData.forEach((prescription) => {
        const productId = prescription.product.id;
        if (productCount[productId] > 1) {
          prescription.flag = true;
        }
      });

      setState({
        ...state,
        showResult: true,
        data: responseData,
      });
      setIsFetching(false);
      return true;
    } catch (error: any) {
      setState({
        ...state,
        error: error.error,
      });
      setIsFetching(false);
      return false;
    }
  };

  const confirmDelete = () => {
    if (!prescriptionId || !user?.access_token) {
      console.error('Id or access token not found');
      return;
    }

    deletePrescription(prescriptionId, user.access_token)
      .then((res) => {
        setOpen(false);
        if (res.status === 200) {
          showToast({
            message: res.data.message,
            type: 'success',
          });
          setFilter({ label: 'Active', value: 'active' });
        } else {
          showToast({
            message: res.data.message,
            type: 'warning',
          });
        }
      })
      .catch((err: any) => {
        console.error(err);
        setOpen(false);
        showToast({
          message: 'Failed to delete prescription. Please try again later.',
          type: 'error',
        });
      });
  };

  const requestForDelete = () => {
    if (!reason) {
      showToast({
        type: 'warning',
        message: 'Empty reason not allowed',
      });

      return;
    }

    if (!prescriptionId) {
      console.error('Prescription Id not found');
      return;
    }
    const data = {
      prescriptionId,
      reason,
    };

    requestForDeletePrescription(data, user?.access_token)
      .then((res) => {
        if (res.status === 200) {
          showToast({
            type: 'success',
            message: res.data.message,
          });
        } else {
          showToast({
            type: 'warning',
            message: res.data.message,
          });
        }
        setRequestOpen(false);
      })
      .catch((err) => {
        showToast({
          type: 'error',
          message: 'Failed to Request Delete Request',
        });
        setRequestOpen(false);
        console.error(err);
      });
  };

  useEffect(() => {
    callPrescription();
  }, [filter]);

  useEffect(() => {
    if (!open) {
      setReason('');
      setPrescriptionId(undefined);
    }
  }, [open]);

  useEffect(() => {
    setIsFetching(true);
    const token = user?.access_token;
    if (!triggerPreferable?.id || !token) return;

    updatePrescriptionPreferable(
      triggerPreferable.id,
      !triggerPreferable.isPreferable,
      token,
    ).then((res) => {
      if (res === 200) {
        callPrescription();
      }
    });

    setIsFetching(false);
  }, [triggerPreferable]);

  useEffect(() => {
    if (!isFetching) {
      setLoadingEnable(false);
    } else setLoadingEnable(true);
  }, [isFetching]);

  if (isFetching) {
    return <Loading />;
  }

  async function handlePrescriptionForwarding() {
    if (!triggerPopup?.data || !user?.access_token) {
      console.log('Missing required details');
      return;
    }
    const res = await sendEPrescription(triggerPopup?.data, user?.access_token);
    if (res === 200) {
      showToast({
        message: 'eScript successfully sent.',
        type: 'success',
      });
      return;
    }
    showToast({
      message: 'Unable to trigger eScript',
      type: 'info',
    });
  }

  const DropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <DownCaretIcon />
      </components.DropdownIndicator>
    );
  };

  return (
    <>
      <div>
        <div
          className={`${process.env.VITE_ERX === 'ENABLE' ? 'bg-white' : 'bg-secondary-cornflowerblue-50'} p-8 rounded-md`}
        >
          <div className="flex flex-col md:flex-row justify-between mb-6">
            <div className="mt-3 md:mt-0">
              <Select
                className="w-full md:w-60"
                value={filter}
                onChange={(selected) => setFilter(selected)}
                options={[
                  { label: 'All', value: 'all' },
                  ...filterPrescriptionStatus.filter(
                    (x) =>
                      ![
                        PrescriptionStatus.Stopped,
                        PrescriptionStatus.Cancelled,
                      ].includes(x.value as PrescriptionStatus),
                  ),
                ]}
                isSearchable={false}
                components={{
                  DropdownIndicator,
                  IndicatorSeparator: () => null,
                }}
              />
            </div>
          </div>
          {state.showResult &&
            state.data.map((pres, idx) => {
              const prescriptionHeaders = [
                'Prescription Qty',
                'Repeat Qty',
                // 'Interval Limit',
              ];
              const prescriptionValues = [
                [
                  pres?.eRx_qty ? pres?.eRx_qty : '-',
                  pres?.prescribed_repeats,
                  // `${pres?.prescribed_interval?.toString() || '0'} days`,
                ],
              ];
              const productHeaders = [
                'Used Monthly Limit',
                'Interval Limit',
                // 'Total Qty Remaining',
                'Repeats Remaining',
              ];
              const productValues = [
                [
                  pres?.monthlyLimit ? pres?.monthlyLimit : 'N/A',
                  `${pres?.prescribed_interval?.toString() || '0'} days`,
                  // pres?.remaining_units,
                  pres?.remaining_repeats <= 0
                    ? 'Last Repeat'
                    : pres?.remaining_repeats,
                ],
              ];
              return (
                <div
                  className="grid grid-cols-1 xl:grid-cols-5 border border-secondary-pink-100 rounded-md mb-5"
                  key={idx}
                >
                  <div
                    className="col-span-1 xl:col-span-2 bg-secondary-pink-50 border-r border-secondary-pink-100 rounded-l-md 
                      px-3.5 xl:px-7 py-3 flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-base xl:text-lg text-primary-blue-500 font-bold">
                          Prescription Details
                        </h3>
                        {pres.token && (
                          <div className="flex">
                            {process.env.VITE_ERX === 'ENABLE' && (
                              <button
                                className="rounded bg-secondary-pink-100 shadow-sm p-2 relative"
                                onClick={() => {
                                  OpenQRmodal({
                                    isModalOpen: true,
                                    token: pres?.token,
                                  });
                                }}
                                onMouseEnter={() =>
                                  setShowTooltip('showQR' + pres.id)
                                }
                                onMouseLeave={() => setShowTooltip(null)}
                                disabled={!pres?.token}
                              >
                                {showTooltip === 'showQR' + pres.id && (
                                  <Tooltip
                                    className="left-1/2 top-11 z-20 transform -translate-x-1/2"
                                    text="Show QR"
                                  />
                                )}
                                <QRicon />
                              </button>
                            )}

                            {pres.status === PrescriptionStatus.Active &&
                              process.env.VITE_ERX === 'ENABLE' && (
                                <button
                                  className="rounded bg-secondary-pink-100 shadow-sm p-2 ml-2 relative"
                                  onClick={() => {
                                    setTrigger({
                                      visibility: true,
                                      data: {
                                        token: pres?.token,
                                        patientName: `${userDetail.first_name} ${userDetail.last_name}`,
                                        productName: pres?.product?.name,
                                      },
                                    });
                                  }}
                                  onMouseEnter={() =>
                                    setShowTooltip('shareScript' + pres.id)
                                  }
                                  onMouseLeave={() => setShowTooltip(null)}
                                >
                                  {showTooltip === 'shareScript' + pres.id && (
                                    <Tooltip
                                      className="left-1/2 top-11 z-20 transform -translate-x-1/2"
                                      text="Share Script"
                                    />
                                  )}
                                  <ShareIcon />
                                </button>
                              )}

                            {process.env.VITE_ERX === 'ENABLE' && (
                              <button
                                className="rounded bg-secondary-pink-100 shadow-sm p-2 ml-2 relative"
                                onClick={() => {
                                  setPrescriptionId(pres?.id);
                                  if (process.env.VITE_ERX === 'ENABLE') {
                                    setOpen(true);
                                  } else {
                                    setRequestOpen(true);
                                  }
                                }}
                                onMouseEnter={() =>
                                  setShowTooltip('cancelScript' + pres.id)
                                }
                                onMouseLeave={() => setShowTooltip(null)}
                              >
                                {showTooltip === 'cancelScript' + pres.id && (
                                  <Tooltip
                                    className="left-1/2 top-11 z-20 transform -translate-x-1/2"
                                    text="Cancel Script"
                                  />
                                )}
                                <CancelScriptIcon />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {pres.comment ? (
                        <div className="text-secondary-pink-700">
                          {process.env.VITE_ERX === 'ENABLE' && (
                            <PrescriptionDetail
                              label="Token:"
                              value={pres?.token ? pres?.token : '-'}
                            />
                          )}
                          <p className="text-left">
                            {pres?.comment ? pres?.comment : '-'}
                          </p>
                        </div>
                      ) : (
                        <div className="text-secondary-pink-700">
                          {process.env.VITE_ERX === 'ENABLE' && (
                            <PrescriptionDetail
                              label="Token:"
                              value={pres?.token ? pres?.token : '-'}
                            />
                          )}

                          <PrescriptionDetail
                            label="Medication Name:"
                            value={pres?.aip_complient_drug_name ?? '-'}
                          />
                          <PrescriptionDetail
                            label="Brand Name:"
                            value={pres?.medication_brand_name ?? '-'}
                          />
                          <PrescriptionDetail
                            label="Prescribed Dose:"
                            value={
                              pres?.prescribed_dosage === ''
                                ? '-'
                                : pres?.prescribed_dosage
                            }
                          />
                          <PrescriptionDetail
                            label="Prescribed by:"
                            value={
                              pres?.doctor
                                ? `Dr. ${pres?.doctor?.user?.first_name} ${pres?.doctor?.user?.last_name}`
                                : '-'
                            }
                          />
                          <PrescriptionDetail
                            label="Prescribed on:"
                            value={
                              pres?.prescription_date &&
                              moment(pres?.prescription_date).isValid()
                                ? `${moment(pres?.prescription_date).format('DD/MM/YYYY')}`
                                : '-'
                            }
                          />
                          <PrescriptionDetail
                            label="Medication Form:"
                            value={pres?.medication_formrtacid ?? '-'}
                          />
                          <PrescriptionDetail
                            label="Medication Strength:"
                            value={pres?.medication_strength ?? '-'}
                          />
                          <PrescriptionDetail
                            label="Route Admin:"
                            value={pres?.route_administration ?? '-'}
                          />
                        </div>
                      )}
                    </div>
                    {!pres.comment && (
                      <SingleRowTable
                        headerBgColor="bg-primary-blue-500"
                        tableWidth="min-w-full"
                        headers={prescriptionHeaders}
                        values={prescriptionValues}
                      />
                    )}
                  </div>
                  <div className="col-span-1 xl:col-span-3 px-7 py-3 bg-white flex flex-col justify-between rounded-r-md">
                    <div>
                      <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center mb-2">
                        <h3 className="text-base xl:text-lg text-secondary-pink-700 font-bold">
                          Matched Product Details
                        </h3>
                        <div className="flex flex-col md:flex-row my-3 xl:my-0 items-start md:items-center">
                          {pres?.flag &&
                            pres?.status === PrescriptionStatus.Active && (
                              <div className="ml-4 flex text-primary-blue-500 items-center sm:mb-3 mb-0">
                                <span className="text-sm font-semibold mt-1 mb-0">
                                  Preferred Prescription?
                                </span>
                                <Toggle
                                  label=""
                                  value={pres.isPreferable as boolean}
                                  onToggle={() => {
                                    setTriggerPreferable({
                                      id: pres.id,
                                      isPreferable: pres.isPreferable,
                                    });
                                  }}
                                  readonly={false}
                                />
                                <div
                                  className="relative ml-3 cursor-pointer"
                                  onMouseEnter={() =>
                                    setShowPreferTooltip(
                                      `preferprescription-${pres.id}`,
                                    )
                                  }
                                  onMouseLeave={() =>
                                    setShowPreferTooltip(undefined)
                                  }
                                >
                                  {showPreferTooltip ===
                                    `preferprescription-${pres.id}` && (
                                    <Tooltip
                                      className="right-0 tooltip-500"
                                      text={`Toggle on to set this as your preferred prescription
                                        for ordering when multiple of the same medication options are available.`}
                                    />
                                  )}
                                  <i
                                    className="fa fa-question-circle-o text-primary-blue-500"
                                    aria-hidden="true"
                                  ></i>
                                </div>
                              </div>
                            )}
                          {pres.status === PrescriptionStatus.Active ? (
                            <div
                              className={`ml-0 md:ml-3 ${pres.isIntervalReached > 0 ? 'bg-primary-red-100' : 'bg-secondary-green-100'}
                              rounded px-2 md:px-4 py-2 inline-flex items-center mb-2 md:mb-0 md:mr-2`}
                            >
                              <span
                                className={`w-2.5 h-2.5 rounded-full mr-2.5 ${
                                  pres?.isIntervalReached > 0
                                    ? 'bg-primary-red-600'
                                    : 'bg-secondary-green-600'
                                }`}
                              ></span>
                              <span className="text-xs md:text-sm font-semibold text-primary-blue-500">
                                Interval{' '}
                                {pres?.isIntervalReached > 0 ? '' : 'not'}{' '}
                                reached
                              </span>
                            </div>
                          ) : (
                            <></>
                          )}
                          {pres.remaining_repeats > 0 ? (
                            <div
                              className={`${renderStatusColor(pres.status)} px-2.5 py-2 rounded flex items-center `}
                            >
                              <span
                                className={`${renderStatusColor(pres.status, false)} rounded-full w-2.5 h-2.5 mr-3 inline-block`}
                              ></span>
                              <span className="text-xs md:text-sm font-semibold text-primary-blue-500">
                                {`${pres?.status.charAt(0).toUpperCase()}${pres?.status.slice(1)}`}
                              </span>
                            </div>
                          ) : null}
                          {/* For Out Of Repeats */}
                          {pres.status === PrescriptionStatus.Active &&
                          pres.remaining_repeats <= 0 &&
                          pres.remaining_units <= 0 ? (
                            <div
                              className={`${renderStatusColor(PrescriptionStatus.Cancelled)} px-2.5 py-2 rounded flex items-center ml-3`}
                            >
                              <span
                                className={`${renderStatusColor(PrescriptionStatus.Cancelled, false)} rounded-full w-2.5 h-2.5 mr-3 inline-block`}
                              ></span>
                              <span className="text-sm font-semibold text-primary-blue-500">
                                Out of Repeats
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-5 mb-14">
                        <div className="col-span-1">
                          <div
                            className="bg-secondary-cornflowerblue-600 text-white font-semibold rounded-full 
                    text-xxs uppercase p-2 inline-block w-max"
                          >
                            {pres?.product?.name ? 'Matched' : 'Unmatched'}{' '}
                            product
                          </div>
                          <div className="mt-1.5">
                            <img
                              className="w-10/12 xl:w-28"
                              src={
                                pres?.product?.image_url === ''
                                  ? '/default-image.png'
                                  : !pres?.product?.image_url
                                    ? '/default-image.png'
                                    : pres?.product?.image_url
                              }
                              alt="product image"
                            />
                          </div>
                        </div>
                        <div className="col-span-1 md:col-span-4">
                          <div className="text-primary-blue-500">
                            <PrescriptionDetail
                              label="Product Name:"
                              value={
                                pres?.product?.name ? pres?.product?.name : '-'
                              }
                            />
                            <PrescriptionDetail
                              label="Script Number:"
                              value={
                                pres?.prescription_code
                                  ? pres?.prescription_code
                                  : '-'
                              }
                            />
                            <PrescriptionDetail
                              label="Prescription Expiry:"
                              value={
                                pres?.comment
                                  ? '-'
                                  : moment(pres?.prescription_expiry).isValid()
                                    ? moment(pres?.prescription_expiry).format(
                                        'DD-MM-YY',
                                      )
                                    : '-'
                              }
                            />
                            <PrescriptionDetail
                              label="Product Details:"
                              value={pres?.product?.description}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    {pres.status === PrescriptionStatus.Active && (
                      <SingleRowTable
                        headerBgColor="bg-secondary-pink-700"
                        tableWidth=""
                        headers={productHeaders}
                        values={productValues}
                      />
                    )}
                  </div>
                </div>
              );
            })}

          {/* {process.env.VITE_ERX === 'ENABLE' ? (
        
          ) : (
            <div className="flex flex-col">
              {state.showResult &&
                state.data.map((pres) => (
                  <>
                    <div
                      className="bg-white rounded-md p-4 md:p-7 relative"
                      key={pres?.id}
                    >
                      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
                        <div className="col-span-1 xl:col-span-2 flex flex-col md:flex-row">
                          <div className="w-full md:w-1/4 flex flex-col justify-center items-center">
                            <span
                              className="bg-secondary-cornflowerblue-600 text-white font-semibold rounded-full 
                        text-xxs uppercase p-2 inline-block w-max"
                            >
                              Matched product
                            </span>

                            <img
                              className="w-10/12 xl:w-28"
                              src={
                                pres?.product?.image_url === ''
                                  ? '/default-image.png'
                                  : !pres?.product?.image_url
                                    ? '/default-image.png'
                                    : pres?.product?.image_url
                              }
                              alt="product image"
                            />
                            {process.env.VITE_ERX === 'ENABLE' &&
                              pres?.token && (
                                <button
                                  className="mt-3 flex items-center w-1/2 md:w-full xl:w-full justify-center"
                                  onClick={() => {
                                    OpenQRmodal({
                                      isModalOpen: true,
                                      token: pres?.token,
                                    });
                                  }}
                                  disabled={!pres?.token}
                                >
                                  <EyeIcon className="w-4 2xl:w-5" />
                                  <span className="ml-1 2xl:ml-3 text-sm 2xl:text-base text-secondary-pink-600 font-regular">
                                    VIEW QR
                                  </span>
                                </button>
                              )}
                          </div>
                          <div className="w-full md:w-3/4">
                            <p className="text-lg md:text-2xl text-primary-blue-500 font-semibold mt-3 md:mt-0">
                              {pres?.product?.name}
                            </p>
                            <p className="text-sm font-regular text-secondary-pink-700 mt-2 mb-6">
                              <span>{pres?.prescribed_dosage} </span>
                            </p>
                            {process.env.VITE_ERX === 'ENABLE' && (
                              <div className="bg-grey-50 px-3 py-2 rounded flex items-center w-full 2xl:w-80 shadow mt-3">
                                <p className="flex border-r border-primary-blue-500 pr-3 items-center w-2/5">
                                  <MedicationsBlueIcon />
                                  <span className="text-sm text-primary-blue-500 font-semibold ml-1">
                                    Number
                                  </span>
                                </p>
                                <p className="text-sm text-primary-blue-500 font-regular pl-3 w-3/5">
                                  {pres?.token ? pres?.token : '-'}
                                </p>
                              </div>
                            )}

                            <div className="bg-grey-50 px-3 py-2 rounded flex items-center w-full 2xl:w-80 shadow mt-3">
                              <p className="flex border-r border-primary-blue-500 w-2/5 pr-3 items-center">
                                <MedicationsBlueIcon />
                                <span className="text-sm text-primary-blue-500 font-semibold ml-1">
                                  Prescriber
                                </span>
                              </p>
                              <p className="text-sm text-primary-blue-500 font-regular pl-3 w-3/5">
                                {pres?.doctor
                                  ? `Dr. ${pres?.doctor?.user?.first_name} ${pres?.doctor?.user?.last_name}`
                                  : '-'}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="col-span-1 xl:col-span-3">
                          <div className="flex justify-end flex-col md:flex-row">
                            <div className="bg-secondary-tangerine-50 rounded px-2 md:px-4 py-2 inline-flex items-center">
                              <span className="w-2.5 h-2.5 bg-primary-red-600 rounded-full mr-2.5"></span>
                              <span className="text-xs md:text-sm font-semibold text-primary-blue-500">
                                Interval{' '}
                                {pres?.isIntervalReached > 0 ? '' : 'not'}{' '}
                                reached
                              </span>
                            </div>
                            <div className="bg-secondary-green-50 rounded px-2 md:px-4 py-2 inline-flex items-center md:mx-4 mt-3 md:mt-0">
                              <span className="w-2.5 h-2.5 bg-secondary-green-600 rounded-full mr-2.5"></span>
                              <span className="text-xs md:text-sm font-semibold text-primary-blue-500">
                                {`${pres?.status.charAt(0).toUpperCase()}${pres?.status.slice(1)}`}
                              </span>
                            </div>
                            <div className="ml-5 absolute top-4 right-4">
                              <Menu
                                as="div"
                                className="relative ml-2 flex items-center"
                              >
                                <Menu.Button className='className="relative flex shadow-none items-center text-sm ml-4"'>
                                  <MoreOptionComponentIcon />
                                </Menu.Button>
                                <Transition
                                  enter="transition ease-out duration-100"
                                  enterFrom="transform opacity-0 scale-95"
                                  enterTo="transform opacity-100 scale-100"
                                  leave="transition ease-in duration-75"
                                  leaveFrom="transform opacity-100 scale-100"
                                  leaveTo="transform opacity-0 scale-95"
                                >
                                  <Menu.Items
                                    className="absolute top-full right-0 lg:right-full z-10 mt-2 origin-top-right rounded-lg 
        p-1 bg-secondary-pink-50 shadow-sm w-44 focus:outline-none flex flex-col"
                                  >
                                    <Menu.Item>
                                      <button
                                        className="flex items-center p-2 text-base font-regular text-primary-blue-500 
                                    hover:bg-secondary-cornflowerblue-100 
                                    hover:rounded-md hover:font-semibold"
                                        onClick={() => {
                                          setPrescriptionId(pres?.id);
                                          setOpen(true);
                                        }}
                                      >
                                        <CancelScriptIcon className="mr-3 w-5" />
                                        <span>Cancel Script</span>
                                      </button>
                                    </Menu.Item>

                                    {pres?.token && (
                                      <Menu.Item>
                                        <button
                                          className="flex items-center p-2 text-base font-regular text-primary-blue-500 
                                      hover:bg-secondary-cornflowerblue-100 hover:rounded-md hover:font-semibold"
                                          onClick={() => {
                                            setTrigger({
                                              visibility: true,
                                              data: {
                                                token: pres?.token,
                                                patientName: `${userDetail.first_name} ${userDetail.last_name}`,
                                                productName:
                                                  pres?.product?.name,
                                              },
                                            });
                                          }}
                                        >
                                          <ShareIcon className="mr-3 w-5" />
                                          <span>Share script</span>
                                        </button>
                                      </Menu.Item>
                                    )}
                                  </Menu.Items>
                                </Transition>
                              </Menu>
                            </div>
                          </div>
                          <div className="flex justify-start md:justify-end items-center mt-6">
                            <label className="text-secondary-pink-600 text-base font-semibold">
                              Expiry Date
                            </label>
                            <p className="text-primary-blue-500 text-sm font-regular ml-10 mr-4">
                              {pres?.comment
                                ? '-'
                                : moment(pres?.prescription_expiry).isValid()
                                  ? moment(pres?.prescription_expiry).format(
                                      'DD-MM-YY',
                                    )
                                  : '-'}
                            </p>
                          </div>
                          {pres?.comment ? (
                            <p className="text-sm text-primary-blue-500 font-regular">
                              {pres?.comment}
                            </p>
                          ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mt-8">
                              <div className="col-span-1 xl:text-center">
                                <label className="text-base text-primary-blue-500 font-semibold">
                                  Prescribed
                                  <br /> Qty
                                </label>
                                <p className="text-sm text-primary-blue-500 font-regular">
                                  {pres?.prescribed_quantity}
                                </p>
                              </div>
                              <div className="col-span-1 xl:text-center">
                                <label className="text-base text-primary-blue-500 font-semibold">
                                  Total
                                  <br /> Repeats
                                </label>
                                <p className="text-sm text-primary-blue-500 font-regular">
                                  {pres?.prescribed_repeats}
                                </p>
                              </div>
                              <div className="col-span-1 xl:text-center">
                                <label className="text-base text-primary-blue-500 font-semibold">
                                  Repeats
                                  <br /> Remaining
                                </label>
                                <p className="text-sm text-primary-blue-500 font-regular">
                                  {pres?.remaining_repeats ?? '-'}
                                </p>
                              </div>
                              <div className="col-span-1 xl:text-center">
                                <label className="text-base text-primary-blue-500 font-semibold">
                                  Interval
                                  <br /> Limit
                                </label>
                                <p className="text-sm text-primary-blue-500 font-regular">
                                  <span>{pres?.prescribed_interval}</span> days
                                </p>
                              </div>
                              <div className="col-span-1 xl:text-center">
                                <label className="text-base text-primary-blue-500 font-semibold">
                                  Monthly
                                  <br /> Limit
                                </label>
                                <p className="text-sm text-primary-blue-500 font-regular">
                                  {pres?.monthlyLimit}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="bg-secondary-pink-50 p-5 mb-3">
                      <h6 className="mb-5  xl:text-xl font-bold text-primary-blue-500">
                        Prescription Details
                      </h6>
                      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-4 ">
                        <div className="col-span-1">
                          <label className="text-base text-primary-blue-500 font-semibold">
                            Medication Name
                          </label>
                          <p className="text-sm text-primary-blue-500 font-regular">
                            {pres?.aip_complient_drug_name ?? '-'}
                          </p>
                        </div>
                        <div className="col-span-1">
                          <label className="text-base text-primary-blue-500 font-semibold">
                            Brand Name
                          </label>
                          <p className="text-sm text-primary-blue-500 font-regular">
                            {pres?.medication_brand_name ?? '-'}
                          </p>
                        </div>
                        <div className="col-span-1">
                          <label className="text-base text-primary-blue-500 font-semibold">
                            Medication Form
                          </label>
                          <p className="text-sm text-primary-blue-500 font-regular">
                            {pres?.medication_formrtacid ?? '-'}
                          </p>
                        </div>
                        <div className="col-span-1">
                          <label className="text-base text-primary-blue-500 font-semibold">
                            Medication Strength
                          </label>
                          <p className="text-sm text-primary-blue-500 font-regular">
                            {pres?.medication_strength ?? '-'}
                          </p>
                        </div>
                        <div className="col-span-1">
                          <label className="text-base text-primary-blue-500 font-semibold">
                            Route Admin
                          </label>
                          <p className="text-sm text-primary-blue-500 font-regular">
                            {pres?.route_administration ?? '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                ))}
            </div>
          )} */}
        </div>
      </div>

      <CustomModal
        isOpen={!!triggerPopup?.visibility}
        onClose={() => setTrigger(undefined)}
        header={'Email Confirmation Wizard'}
        footer={
          <>
            <button
              className="mr-4 bg-primary-blue-500 text-white px-4 py-2 rounded"
              onClick={() => {
                handlePrescriptionForwarding();
                setTrigger(undefined);
              }}
            >
              Send Email
            </button>
            <button
              className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 px-4 py-2 rounded"
              onClick={() => setTrigger(undefined)}
            >
              Cancel
            </button>
          </>
        }
        modalSize={'w-11/12 md:w-1/2 xl:w-1/3 3xl:w-3/12'}
      >
        <p>Please Enter Email to forward prescription*</p>
        <div className="mt-2">
          <input
            type="email"
            name="email"
            id="reason"
            autoComplete="given-name"
            required
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1
             ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset
              focus:ring-indigo-600 sm:text-sm sm:leading-6"
            value={triggerPopup?.data.toAddress}
            onChange={(e) =>
              setTrigger(
                (p) =>
                  ({
                    ...p,
                    data: { ...p?.data, toAddress: e.target.value },
                  }) as TriggerPopup,
              )
            }
          />
        </div>
      </CustomModal>

      <CustomModal
        isOpen={open}
        onClose={() => setOpen(false)}
        header={'Confirmation'}
        footer={
          <>
            <button
              className="mr-4 bg-primary-blue-500 text-white px-4 py-2 rounded"
              onClick={() => confirmDelete()}
            >
              Yes
            </button>
            <button
              className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 px-4 py-2 rounded"
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
          </>
        }
        modalSize={'w-11/12 md:w-1/2 xl:w-1/3 3xl:w-3/12'}
      >
        <p>Are you sure you want to delete the Prescription?</p>
      </CustomModal>
      <CustomModal
        isOpen={requestOpen}
        onClose={() => setRequestOpen(false)}
        header={'Request Delete Prescription'}
        footer={
          <>
            <button
              className="mr-4 bg-primary-blue-500 text-white px-4 py-2 rounded"
              onClick={() => requestForDelete()}
            >
              Yes
            </button>
            <button
              className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 px-4 py-2 rounded"
              onClick={() => setRequestOpen(false)}
            >
              Cancel
            </button>
          </>
        }
        modalSize={'w-3/12'}
      >
        <p>Reason for Delete Prescription</p>
        <div className="mt-2">
          <input
            type="text"
            name="reason"
            id="reason"
            autoComplete="given-name"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 
            ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </CustomModal>
      <CustomModal
        isOpen={(qrModal.isModalOpen && qrModal.token) as boolean}
        onClose={() => OpenQRmodal({ isModalOpen: false, token: undefined })}
        header={'QR Code'}
        footer={undefined}
      >
        <div>
          <CustomQR value={qrModal?.token as string} size={300} />
        </div>
      </CustomModal>
    </>
  );
};

export default PatientPrescriptions;
